import React, { useEffect, useState } from 'react';
import { Container, Draggable } from 'react-smooth-dnd';
import arrayMove from 'array-move';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import Fab from '@mui/material/Fab';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { palette } from '../design-system/colors';
import './orderable-list.scss';
import { QuestionHeading } from './question-heading';

const useStyles = makeStyles(() => ({
    listNumber: {
        background: '#e4f1ec',
        borderRadius: '10px',
        height: '20px',
        width: '20px',
        color: palette.evergreen.aspargus,
        fontWeight: 'bold',
        fontSize: '10px',
        lineHeight: '20px',
        textAlign: 'center',
    },

    listItemText: {
        margin: '0 0 0 10px',
        padding: '15px 10px',
        background: '#fff',
        boxShadow: '0 0 2px rgba(0,0,0,.08)',
        borderRadius: '4px',
        flex: '1 1 auto',
        minWidth: '0',
        '& .MuiListItemText-primary': {
            overflowWrap: 'break-word',
            marginRight: '0',
            maxWidth: '60%',
        },
    },

    orderableListArrows: {
        marginRight: '35px',
        display: 'flex',
    },

    arrowButton: {
        background: 'none',
        //border: 1px solid #1D1E1D;
        boxShadow: 'none',
        '&:focus': {
            outline: 'none',
            boxShadow: 'none',
        },
        disabled: {
            opacity: 0.2,
        },
    },

    orderableListArrow: {
        border: '1px solid #efefef',
        borderRadius: '50%',
        width: '40px',
        '&.MuiFab-sizeSmall': {
            '@media (max-width: 992px)': {},
        },

        '@media (max-width: 992px)': {
            transform: 'scale(0.8)',
        },
    },

    secondaryAction: {
        top: '50%',
        right: '16px',
        position: 'absolute',
        transform: 'translateY(-50%)',
    },

    dragHandle: {
        position: 'absolute',
        top: '8px',
    },

    dragHandleContainer: {
        display: 'flex',
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        minWidth: '50px',
    },

    answerIcon: {
        width: '20px',
        height: '20px',
        position: 'absolute',
        top: '-10px',
        left: '-70px',
        '&.incorrect': {
            background:
                "url('/static/img/assessment/icon-incorrect.svg') no-repeat left center",
        },
        '&.correct': {
            background:
                "url('/static/img/assessment/icon-correct.svg') no-repeat left center",
        },
    },
}));

export const OrderableList = observer(({ ui, handleChange, answer }) => {
    const { t } = useTranslation('assessment');
    const classes = useStyles();

    const shuffleArray = (array) => {
        const arrayCopy = [...array];
        arrayCopy.map((value, i) => {
            return { id: i + 1, value: value.value, text: value.text };
        });
        for (let i = arrayCopy.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arrayCopy[i], arrayCopy[j]] = [arrayCopy[j], arrayCopy[i]];
        }
        return arrayCopy;
    };

    const pickFromAnswer = (array, userOrder) => {
        const arrayCopy = [...array];
        arrayCopy.map((value, i) => {
            return { id: i + 1, value: value.value, text: value.text };
        });
        const resultArray = [];
        userOrder.forEach((value) => {
            resultArray.push(arrayCopy.find((obj) => obj.value === value));
        });
        return resultArray;
    };

    //const [items, setItems] = useState(answer ? pickFromAnswer(ui.options, JSON.parse(answer.userAnswer.value)) : shuffleArray(ui.options));
    const [items, setItems] = useState(
        answer && answer.userAnswer.value
            ? pickFromAnswer(ui.options, JSON.parse(answer.userAnswer.value))
            : shuffleArray(ui.options)
    );

    useEffect(() => {
        if (handleChange) handleChange(getItemsValues());
    }, [items]);

    const onDrop = ({ removedIndex, addedIndex }) => {
        setItems((items) => arrayMove(items, removedIndex, addedIndex));
    };

    const onMoveUp = (index) => {
        if (index > 0) {
            onDrop({ removedIndex: index, addedIndex: index - 1 });
        }
    };

    const onMoveDown = (index, itemsLength) => {
        if (index < itemsLength) {
            onDrop({ removedIndex: index, addedIndex: index + 1 });
        }
    };

    const getItemsValues = () => {
        let result = [];
        items.forEach((item, i) => {
            result[i] = item.value;
        });
        return result;
    };

    return (
        <div className={classnames('assessment')}>
            <div className={'assessment-question'}>
                <QuestionHeading ui={ui} />
                {!answer ? (
                    <List>
                        <Container
                            dragHandleSelector=".drag-handle"
                            lockAxis="y"
                            onDrop={onDrop}
                        >
                            {items.map((item, i) => (
                                <Draggable key={i}>
                                    <ListItem>
                                        <div
                                            className={classnames(
                                                classes.listNumber
                                            )}
                                        >
                                            {i + 1}
                                        </div>
                                        <ListItemText
                                            className={classnames(
                                                classes.listItemText
                                            )}
                                            primary={t(item.text)}
                                        />
                                        <ListItemSecondaryAction
                                            className={classnames(
                                                classes.secondaryAction
                                            )}
                                        >
                                            <div
                                                className={classnames(
                                                    classes.orderableListArrows
                                                )}
                                            >
                                                <ListItemIcon
                                                    className={classnames({
                                                        disabled: i === 0,
                                                    })}
                                                    disabled={i === 0}
                                                    onClick={() => onMoveUp(i)}
                                                >
                                                    <div
                                                        className={classnames(
                                                            classes.orderableListArrow
                                                        )}
                                                    >
                                                        <Fab
                                                            className={classnames(
                                                                classes.arrowButton
                                                            )}
                                                            size={'small'}
                                                        >
                                                            <ExpandLess />
                                                        </Fab>
                                                    </div>
                                                </ListItemIcon>
                                                <ListItemIcon
                                                    className={classnames({
                                                        disabled:
                                                            i >= items.length,
                                                    })}
                                                    disabled={i >= items.length}
                                                    onClick={() =>
                                                        onMoveDown(
                                                            i,
                                                            items.length
                                                        )
                                                    }
                                                >
                                                    <div
                                                        className={classnames(
                                                            classes.orderableListArrow
                                                        )}
                                                    >
                                                        <Fab
                                                            size={'small'}
                                                            className={classnames(
                                                                classes.arrowButton
                                                            )}
                                                        >
                                                            <ExpandMore />
                                                        </Fab>
                                                    </div>
                                                </ListItemIcon>
                                            </div>
                                            <ListItemIcon
                                                className="drag-handle"
                                                style={{ cursor: 'ns-resize' }}
                                            >
                                                <DragHandleIcon
                                                    className={classnames(
                                                        classes.dragHandle
                                                    )}
                                                />
                                            </ListItemIcon>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                </Draggable>
                            ))}
                        </Container>
                    </List>
                ) : answer.userAnswer.value ? (
                    <List>
                        {items.map((item, i) => (
                            <ListItem key={i}>
                                <div className={classnames(classes.listNumber)}>
                                    {i + 1}
                                </div>
                                <ListItemText
                                    className={classnames(classes.listItemText)}
                                    primary={t(item.text)}
                                />
                                <ListItemSecondaryAction>
                                    {answer.correctAnswer.value[i] ===
                                        JSON.parse(answer.userAnswer.value)[
                                            i
                                        ] && (
                                        <div
                                            className={classnames(
                                                classes.answerIcon,
                                                'correct'
                                            )}
                                        />
                                    )}
                                    {answer.correctAnswer.value[i] !==
                                        JSON.parse(answer.userAnswer.value)[
                                            i
                                        ] && (
                                        <div
                                            className={classnames(
                                                classes.answerIcon,
                                                'incorrect'
                                            )}
                                        />
                                    )}
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <p>{t("You didn't answer this question.")}</p>
                )}
            </div>
        </div>
    );
});
